import {
    DataTag,
    QueryKey,
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from "@tanstack/react-query";
import { useDeviceGroupsODataHook } from "../device-group-http-functions";
import useCustomFetchClient, { ErrorType } from "../mutator/custom-fetch";
import { DeviceDto, DeviceGroupDto } from "../query.schemas";
import { useDeviceListODataHook } from "../device-http-functions";

export function useDeviceGroupsOData<
    TData = Awaited<ReturnType<ReturnType<typeof useDeviceGroupsODataHook>>>,
    TError = ErrorType<unknown>,
>(options?: {
    queryParams?: Record<string, string>;
    query?: Partial<
        UseQueryOptions<
            Awaited<ReturnType<ReturnType<typeof useDeviceGroupsODataHook>>>,
            TError,
            TData
        >
    >;
}): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const deviceGroupsOData = useCustomFetchClient<DeviceGroupDto[]>();

    const queryKey = (options?.query?.queryKey ?? [
        `/device-groups-odata`,
        options?.queryParams,
    ]) as DataTag<QueryKey, TData, TError>;

    const query = useQuery({
        queryKey,
        queryFn: () =>
            deviceGroupsOData({
                url: `/device-groups-odata`,
                params: options?.queryParams,
                method: "GET",
            }),
        ...options?.query,
    }) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryKey;

    return query;
}

export function useDevicesOData<
    TData = Awaited<ReturnType<ReturnType<typeof useDeviceListODataHook>>>,
    TError = ErrorType<unknown>,
>(options?: {
    queryParams?: Record<string, string>;
    query?: Partial<
        UseQueryOptions<
            Awaited<ReturnType<ReturnType<typeof useDeviceListODataHook>>>,
            TError,
            TData
        >
    >;
}): UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
} {
    const deviceListOData = useCustomFetchClient<DeviceDto[]>();

    const queryKey = (options?.query?.queryKey ?? [
        `/devices-odata`,
        options?.queryParams,
    ]) as DataTag<QueryKey, TData, TError>;

    const query = useQuery({
        queryKey,
        queryFn: () =>
            deviceListOData({
                url: `/devices-odata`,
                params: options?.queryParams,
                method: "GET",
            }),
        ...options?.query,
    }) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    query.queryKey = queryKey;

    return query;
}
